import { useState, useEffect, useContext, useRef } from 'react';
import {
    Row,
    Col,
    Button,
    ButtonGroup,
    Modal,
    ModalBody,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import {
    TextInput,
    CheckboxInput,
    SelectInput,
    stateOptions,
} from '~/shared/components/form/Inputs';
import { useForm } from 'react-hook-form';
import { PaymentOptions } from '~/enums/PaymentOptions.enum';
import {
    createchangerequest,
    achDocumentPresignedUrlByCampaign,
} from '~/services/campaign';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FundRequestContext } from '~/context/fundrequests';
import { uploadFile, resizeImage } from '~/services/fundrequests';
import { CampaignContext } from '~/context/campaign';
import { normalizeFileName } from '~/shared/utils';

const paymentOptionsArray = [
    { label: 'Check', value: PaymentOptions.CHECK },
    { label: 'Direct Deposit (ACH)', value: PaymentOptions.ACH },
];

// Match values with AchInfoForm.tsx
const achAccountTypeOptions = [
    { label: 'Checking', value: 'CHECKING' },
    { label: 'Savings', value: 'SAVINGS' },
];

// Match values with AchInfoForm.tsx
const achDocumentTypeOptions = [
    { label: 'A voided check', value: 'VOID_CHECK' },
    {
        label: 'Letter or screenshot from bank stating account and routing number',
        value: 'BANK_LETTER',
    },
];

export type ChangeRequestModalProps = {
    isOpen: Boolean;
    toggle: () => void;
    payeeName: string;
    onSuccess?: () => void;
};

export const ChangeRequestModal = (props: ChangeRequestModalProps) => {
    const { isOpen, toggle, payeeName } = props;
    const [{ selectedPayee }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [addressOptions, setAddressOptions] = useState<any[]>([]);
    const { vendorId } = useParams<{ vendorId: string }>();
    const {
        watch,
        control,
        reset,
        getValues,
        setValue,
        handleSubmit,
        setError,
        clearErrors,
        formState: { isDirty, dirtyFields, isSubmitting, errors },
    } = useForm();

    // Use state for payment method selection
    const [paymentMethod, setPaymentMethod] = useState<string>('');

    // Track original address values
    const [originalAddressValues, setOriginalAddressValues] = useState<any>({});
    const [addressFieldsChanged, setAddressFieldsChanged] = useState(false);
    const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);

    const selected_address = watch('requested_address');
    const address_label = watch('address_label');
    const address_1 = watch('address_1');
    const address_2 = watch('address_2');
    const city = watch('city');
    const state = watch('state');
    const zip_code = watch('zip_code');
    const delete_address = watch('delete_address');
    const make_primary = watch('make_primary');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [achFile, setAchFile] = useState<File | null>(null);

    // Prepare address options for dropdown when component mounts or selectedPayee changes
    useEffect(() => {
        if (
            selectedPayee &&
            selectedPayee.vendor_addresses &&
            selectedPayee.vendor_addresses.length > 0
        ) {
            const options = selectedPayee.vendor_addresses.map(
                (address: any) => ({
                    value: address.id,
                    label: address.address_label || 'Address',
                    data: address,
                })
            );
            setAddressOptions(options);
        }
    }, [selectedPayee, isOpen]);

    // Set default form values from selectedPayee when modal opens
    useEffect(() => {
        if (isOpen && selectedPayee) {
            // Set default values for name, email, and phone
            setValue('name', selectedPayee.name || '');
            setValue('email', selectedPayee.email || '');
            setValue('phone', selectedPayee.phone || '');

            // Set payment_type if available
            if (selectedPayee.payment_type) {
                setValue('payment_type', selectedPayee.payment_type);
            }
        }
    }, [isOpen, selectedPayee, setValue]);

    // Update form when an address is selected
    useEffect(() => {
        if (selected_address) {
            const addressData = addressOptions.find(
                (option) => option.value === selected_address
            )?.data;
            if (addressData) {
                const addressValues = {
                    address_label: addressData.address_label || '',
                    address_1: addressData.address_1 || '',
                    address_2: addressData.address_2 || '',
                    city: addressData.city || '',
                    state: addressData.state?.state || '',
                    zip_code: addressData.zip_code || '',
                };

                // Store original values
                setOriginalAddressValues(addressValues);

                // Set form values
                setValue('address_label', addressValues.address_label);
                setValue('address_1', addressValues.address_1);
                setValue('address_2', addressValues.address_2);
                setValue('city', addressValues.city);
                setValue('state', addressValues.state);
                setValue('zip_code', addressValues.zip_code);

                // Reset change tracking
                setAddressFieldsChanged(false);

                // Reset checkbox values
                setValue('delete_address', false);
                setValue('make_primary', false);

                // Check if this is the primary address
                setIsPrimaryAddress(addressData.is_primary || false);

                // If the address has payment_type data, set it
                if (addressData.payment_type) {
                    setValue('payment_type', addressData.payment_type);
                }
            }
        }
    }, [selected_address, addressOptions, setValue]);

    // Check for changes in address fields
    useEffect(() => {
        if (selected_address && originalAddressValues) {
            const hasAddressChanged =
                address_label !== originalAddressValues.address_label ||
                address_1 !== originalAddressValues.address_1 ||
                address_2 !== originalAddressValues.address_2 ||
                city !== originalAddressValues.city ||
                state !== originalAddressValues.state ||
                zip_code !== originalAddressValues.zip_code ||
                delete_address ||
                make_primary;

            setAddressFieldsChanged(hasAddressChanged);
        }
    }, [
        address_label,
        address_1,
        address_2,
        city,
        state,
        zip_code,
        delete_address,
        make_primary,
        selected_address,
        originalAddressValues,
    ]);

    const getButtonStyles = (isSelected: boolean) => ({
        backgroundColor: isSelected ? '#01aeb4' : '',
        borderColor: isSelected ? '#01aeb4' : '',
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setAchFile(e.target.files[0]);
        }
    };

    const triggerFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle payment method change and reset appropriate form fields
    const handlePaymentMethodChange = (method: string) => {
        if (method === paymentMethod) return;

        // Reset fields based on which section is being switched from
        if (method === 'check_address') {
            // Switching to Check Address, reset Direct Deposit fields
            setValue('account_type', '', { shouldDirty: false });
            setValue('document_type', '', { shouldDirty: false });
            // Set payment_type to CHECK when check address is selected
            setValue('payment_type', PaymentOptions.CHECK, { shouldDirty: true });
            setAchFile(null);

            // If we previously had an address selected, restore those values
            if (selected_address) {
                const addressData = addressOptions.find(
                    (option) => option.value === selected_address
                )?.data;
                if (addressData) {
                    setValue('address_1', addressData.address_1 || '', { shouldDirty: true });
                    setValue('address_2', addressData.address_2 || '', { shouldDirty: true });
                    setValue('city', addressData.city || '', { shouldDirty: true });
                    setValue('state', addressData.state?.state || '', { shouldDirty: true });
                    setValue('zip_code', addressData.zip_code || '', { shouldDirty: true });
                }
            }
        } else {
            // Switching to Direct Deposit, reset Check Address fields
            setValue('requested_address', '', { shouldDirty: false });
            setValue('address_1', '', { shouldDirty: false });
            setValue('address_2', '', { shouldDirty: false });
            setValue('city', '', { shouldDirty: false });
            setValue('state', '', { shouldDirty: false });
            setValue('zip_code', '', { shouldDirty: false });
            setValue('delete_address', false, { shouldDirty: false });
            setValue('make_primary', false, { shouldDirty: false });
            // Set payment_type to ACH when direct deposit is selected
            setValue('payment_type', PaymentOptions.ACH, { shouldDirty: true });
        }

        setPaymentMethod(method);
    };

    const onSubmit = async (data: any) => {
        const {
            city,
            email,
            name,
            phone,
            state,
            address_1,
            address_2,
            zip_code,
            requested_address,
            account_type,
            document_type,
            delete_address,
            make_primary,
        } = data;

        const formdata: any = {
            name,
            email,
            phone,
            address_label: data.address_label,
            address_1,
            address_2,
            city,
            state,
            zip_code,
            vendor_address: requested_address,
            delete_address,
            make_primary,
        };

        const campaignId = campaign?.campaignDetail?.campaign_id;

        // Add banking info if Direct Deposit is selected
        if (paymentMethod === 'direct_deposit') {
            try {
                // Map form values to ACH request values
                formdata.account_type = account_type;
                formdata.document_type = document_type;
                formdata.agreement_signed = true;

                // Upload ACH document if selected
                if (achFile && campaignId) {
                    // Normalize the filename to replace spaces and special characters
                    const normalizedFilename = normalizeFileName(achFile);
                    const { fields, url } =
                        await achDocumentPresignedUrlByCampaign(
                            campaignId,
                            normalizedFilename
                        );

                    // Resize the image if it's an image file, otherwise use original file
                    const fileType = achFile.type.split('/')[0];
                    const fileToUpload = fileType === 'image'
                        ? await resizeImage(achFile)
                        : achFile;

                    await uploadFile(fields, url, fileToUpload);
                    formdata.ach_document_url = `${url}ach/${campaignId}/${normalizedFilename}`;
                    formdata.ach_document_filename = normalizedFilename;
                }
            } catch (error) {
                console.error('Error uploading file:', error);
                toast.error('Error uploading file');
                return;
            }
        }

        try {
            await createchangerequest(formdata, vendorId);
            reset();
            setAchFile(null);
            setPaymentMethod('check_address');
            toggle();
            toast.success('Change request submitted successfully');
            // Call the onSuccess callback if provided
            if (props.onSuccess) {
                props.onSuccess();
            }
        } catch (error) {
            toast.error('Error submitting change request');
        }
    };

    // Determine if the submit button should be disabled
    const isSubmitDisabled = () => {
        // Always disabled when submitting
        if (isSubmitting) {
            return true;
        }

        // Check if any of the main contact fields have changed
        const hasContactChanged =
            dirtyFields.name || dirtyFields.email || dirtyFields.phone;

        // Check payment method specific requirements
        if (paymentMethod === 'check_address') {
            // If Check Address is selected, we need an address selected AND either
            // contact info changed OR address fields changed
            const isCheckAddressValid = (
                selected_address &&
                (hasContactChanged || addressFieldsChanged)
            );

            return !isCheckAddressValid;
        } else if (paymentMethod === 'direct_deposit') {
            // For Direct Deposit, we need account type, document type, payment type and file
            const account_type = watch('account_type');
            const document_type = watch('document_type');
            const payment_type = watch('payment_type');

            // If ANY contact field has changed OR all ACH fields are filled, enable the button
            if (hasContactChanged) {
                return false; // Enable button if contact info changed
            }

            // Check if all required direct deposit fields are filled
            const allFieldsFilled = !!(account_type && document_type && achFile);
            return !allFieldsFilled;
        }

        return !isDirty;
    };

    return (
        <Modal centered isOpen={isOpen as boolean} toggle={toggle}>
            <ModalBody>
                <h3>Change Request for {payeeName}</h3>
                <p>
                    Please specify what you would like to change and we will do
                    our best to fulfill your request.
                </p>

                {/* Always show Name, Email, Phone at the top */}
                <FormGroup>
                    <TextInput
                        label='Name'
                        name='name'
                        type='text'
                        control={control}
                    />

                    <TextInput
                        label='Email Address'
                        name='email'
                        type='email'
                        control={control}
                    />

                    <TextInput
                        label='Phone Number'
                        name='phone'
                        type='tel'
                        control={control}
                    />
                </FormGroup>

                {/* Payment Method Options */}
                <FormGroup className='mb-4'>
                    {/* Check Address Section */}
                    <div className='form-check mb-2'>
                        <input
                            id='check_address_radio'
                            type='radio'
                            className='form-check-input'
                            checked={paymentMethod === 'check_address'}
                            onChange={() =>
                                handlePaymentMethodChange('check_address')
                            }
                            name='payment_method_radio'
                        />
                        <label
                            className='form-check-label'
                            htmlFor='check_address_radio'
                        >
                            <strong>Check Address</strong>
                        </label>
                    </div>

                    {paymentMethod === 'check_address' && (
                        <div className='ms-4'>
                            {/* Requested Address dropdown for selecting a vendor address */}
                            <SelectInput
                                control={control}
                                name='requested_address'
                                label='Requested Address'
                                options={addressOptions}
                                required={true}
                            />

                            {selected_address && (
                                <div
                                    className='mt-3'
                                    style={{
                                        borderLeft: '1px solid #dee2e6',
                                        paddingLeft: '1rem',
                                        marginLeft: '0',
                                    }}
                                >
                                    <TextInput
                                        label='Address Label'
                                        name='address_label'
                                        type='text'
                                        control={control}
                                    />
                                    <TextInput
                                        label='Street Address'
                                        name='address_1'
                                        type='text'
                                        control={control}
                                    />
                                    <TextInput
                                        label='Street Address 2'
                                        name='address_2'
                                        type='text'
                                        control={control}
                                    />
                                    <TextInput
                                        label='City'
                                        name='city'
                                        type='text'
                                        control={control}
                                    />
                                    <Row>
                                        <Col sm='8'>
                                            <SelectInput
                                                control={control}
                                                name='state'
                                                label='State'
                                                options={stateOptions}
                                            />
                                        </Col>
                                        <Col sm='4'>
                                            <TextInput
                                                name='zip_code'
                                                label='Zip Code'
                                                type='zip'
                                                control={control}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Address action checkboxes */}

                                    <div className='mt-3'>
                                        {!isPrimaryAddress && (
                                            <>
                                                <CheckboxInput
                                                    id='delete_address'
                                                    name='delete_address'
                                                    label='Delete this address'
                                                    type='checkbox'
                                                    control={control}
                                                    value={true}
                                                    disabled={make_primary}
                                                />

                                                <CheckboxInput
                                                    id='make_primary'
                                                    name='make_primary'
                                                    label='Make this my primary check address'
                                                    type='checkbox'
                                                    control={control}
                                                    value={true}
                                                    disabled={delete_address}
                                                />
                                            </>
                                        )}
                                        {isPrimaryAddress && (
                                            <div className='text-muted mt-1 small'>
                                                <em>
                                                    This is already your primary
                                                    address and cannot be
                                                    deleted.
                                                </em>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Direct Deposit Section */}
                    <div className='form-check mb-2 mt-4'>
                        <input
                            id='direct_deposit_radio'
                            type='radio'
                            className='form-check-input'
                            checked={paymentMethod === 'direct_deposit'}
                            onChange={() => {
                                handlePaymentMethodChange('direct_deposit');
                            }}
                            name='payment_method_radio'
                        />
                        <label
                            className='form-check-label'
                            htmlFor='direct_deposit_radio'
                        >
                            <strong>Direct Deposit</strong>
                        </label>
                    </div>

                    {paymentMethod === 'direct_deposit' && (
                        <div className='ms-4'>

                            <Label>
                                Account Type <span className='required'>*</span>
                            </Label>
                            <div className='d-flex gap-3'>
                                <CheckboxInput
                                    id='checking'
                                    name='account_type'
                                    label='Checking'
                                    type='radio'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    value={achAccountTypeOptions[0].value}
                                />
                                <CheckboxInput
                                    id='savings'
                                    name='account_type'
                                    label='Savings'
                                    type='radio'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    value={achAccountTypeOptions[1].value}
                                />
                            </div>
                            <Label>
                                I am providing one of the following:{' '}
                                <span className='required'>*</span>
                            </Label>
                            <div>
                                <CheckboxInput
                                    id='voided_check'
                                    name='document_type'
                                    label='A voided check'
                                    type='radio'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    value={achDocumentTypeOptions[0].value}
                                />
                                <CheckboxInput
                                    id='letter_screenshot'
                                    name='document_type'
                                    label='Letter or screenshot from bank stating account and routing number'
                                    type='radio'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    value={achDocumentTypeOptions[1].value}
                                />
                            </div>
                            <input
                                type='file'
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                accept='image/*,application/pdf'
                            />
                            <Button
                                color='primary'
                                size='sm'
                                className='mb-3'
                                onClick={() => {
                                    fileInputRef.current?.click();
                                }}
                            >
                                {achFile ? 'Change File' : 'Upload File'}
                            </Button>
                            {achFile && (
                                <div className='mt-2 mb-2'>
                                    <span className='fw-bold'>
                                        Selected file:
                                    </span>{' '}
                                    {achFile.name}
                                    <Button
                                        color='link'
                                        size='sm'
                                        className='ms-2 p-0'
                                        onClick={() => setAchFile(null)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </FormGroup>

                <Button color='outline-dark' onClick={() => {
                    // Reset form values
                    reset();
                    // Reset other state
                    setPaymentMethod('');
                    setAchFile(null);
                    toggle();
                }}>
                    Cancel
                </Button>
                <Button
                    color='primary'
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitDisabled()}
                >
                    {isSubmitting ? 'Saving...' : 'Submit'}
                </Button>
            </ModalBody>
        </Modal>
    );
};
